import type { ConversationMessageModel, ConversationModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { PendingApprovalCall, ToolApprovalRecord } from "#types/ToolApproval.ts";
import type { QueryClient } from "@tanstack/react-query";

interface ApprovalAnswer {
    // The generated decision is a plain string; these are the values a client writes.
    decision: "allow" | "deny" | "cancelled";
    // The user's edits to an approved call; the card shows them until the next read.
    editedArgs?: Record<string, unknown>;
}

const isPending = ({ status }: ToolApprovalRecord) => status === "pending";

// The conversation's open approval and the message it shows on.
export function findPendingApproval(conversation: ConversationModel | undefined): { messageId: string; approval: ToolApprovalRecord } | null {
    const message = conversation?.messages.findLast(({ toolApprovals }) => toolApprovals?.some(isPending));
    const approval = message?.toolApprovals?.find(isPending);

    return message && approval ? { messageId: message.id, approval } : null;
}

// Rewrites only the messages `update` changes, so the others keep their identity.
export function updateMessages(queryClient: QueryClient, conversationId: string, update: (message: ConversationMessageModel) => ConversationMessageModel) {
    queryClient.setQueryData<ConversationModel>(AiQueryKeys.GetConversation(conversationId), prev => prev && { ...prev, messages: prev.messages.map(update) });
}

/** Shows a pending approval on a message. A call core asks about again leaves the record it was on, so it has one card. */
export function showPendingApproval(queryClient: QueryClient, conversationId: string, messageId: string, calls: PendingApprovalCall[]) {
    const callIds = new Set(calls.map(({ toolCallId }) => toolCallId));
    const approval: ToolApprovalRecord = { status: "pending", requestedAt: new Date().toISOString(), calls: calls.map(call => ({ ...call, decision: null })) };

    updateMessages(queryClient, conversationId, message => {
        const hasCall = !!message.toolApprovals?.some(({ calls: held }) => held.some(({ toolCallId }) => callIds.has(toolCallId)));
        if (message.id !== messageId && !hasCall) {
            return message;
        }
        const others = (message.toolApprovals ?? [])
            .map(record => ({ ...record, calls: record.calls.filter(({ toolCallId }) => !callIds.has(toolCallId)) }))
            .filter(({ calls: held }) => held.length > 0);

        return { ...message, toolApprovals: message.id === messageId ? [...others, approval] : others };
    });
}

/**
 * Turns a pending approval into an answered card on the message it showed on. The card keeps the prompt's display and
 * arguments; the replay event from the next read replaces it.
 */
export function answerPendingApproval(queryClient: QueryClient, conversationId: string, approval: ToolApprovalRecord, answer: (call: PendingApprovalCall) => ApprovalAnswer) {
    const queryKey = AiQueryKeys.GetConversation(conversationId);
    const callIds = new Set(approval.calls.map(({ toolCallId }) => toolCallId));
    const isThisApproval = (record: ToolApprovalRecord) => isPending(record) && record.calls.some(({ toolCallId }) => callIds.has(toolCallId));
    const answered = (record: ToolApprovalRecord): ToolApprovalRecord => ({
        ...record,
        status: "resolved",
        isAnsweredHere: true,
        calls: record.calls.map(call => {
            const { decision, editedArgs } = answer(call);

            return { ...call, args: { ...call.args, ...editedArgs }, decision, outcome: null };
        })
    });

    // A read already in flight predates the answer, so it must not bring the prompt back.
    void queryClient.cancelQueries({ queryKey });
    updateMessages(queryClient, conversationId, message => (message.toolApprovals?.some(isThisApproval)
        ? { ...message, toolApprovals: message.toolApprovals.map(record => (isThisApproval(record) ? answered(record) : record)) }
        : message));
}

/**
 * Shows every open approval as cancelled when a message is sent past it; core supersedes the open pause on its own.
 * Returns whether any card changed.
 */
export function cancelPendingApproval(queryClient: QueryClient, conversationId: string): boolean {
    const conversation = queryClient.getQueryData<ConversationModel>(AiQueryKeys.GetConversation(conversationId));
    const pending = conversation?.messages.flatMap(({ toolApprovals }) => toolApprovals?.filter(isPending) ?? []) ?? [];
    pending.forEach(approval => answerPendingApproval(queryClient, conversationId, approval, () => ({ decision: "cancelled" })));

    return pending.length > 0;
}
